/**
 * QR Code Polling Service
 * Polls the session endpoint every second to check if QR code was scanned
 */

export interface QRScanStatus {
  scanned: boolean;
  timestamp?: string;
  error?: string;
}

export class QRPollingService {
  private static instance: QRPollingService;
  private pollingInterval: NodeJS.Timeout | null = null;
  private isPolling = false;

  private constructor() {}

  public static getInstance(): QRPollingService {
    if (!QRPollingService.instance) {
      QRPollingService.instance = new QRPollingService();
    }
    return QRPollingService.instance;
  }

  /**
   * Start polling the session endpoint
   * @param sessionId - The session ID to poll
   * @param onScanDetected - Callback when QR code is scanned
   * @param onError - Callback for errors
   */
  public async startPolling(
    sessionId: string,
    onScanDetected: () => void,
    onError?: (error: string) => void
  ): Promise<void> {
    if (this.isPolling) {
      console.warn("QR polling is already active");
      return;
    }

    // Send ready event to server first
    try {
      await this.setStatus(sessionId, "ready");
    } catch (error) {
      console.error("❌ Failed to send ready event:", error);
      if (onError) {
        onError(
          `Failed to notify server: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
      return;
    }

    // Wait 5 seconds before starting polling
    setTimeout(() => {
      this.isPolling = true;

      this.pollingInterval = setInterval(async () => {
        try {
          const status = await this.checkStatus(sessionId);

          if (status.scanned) {
            this.stopPolling();
            onScanDetected();
          } else if (status.error) {
            console.error("❌ Error checking QR scan status:", status.error);
            if (onError) {
              onError(status.error);
            }
          }
        } catch (error) {
          console.error("❌ Error during QR polling:", error);
          if (onError) {
            onError(error instanceof Error ? error.message : "Unknown error");
          }
        }
      }, 2000); // Poll every 2 second
    }, 5000); // Wait 5 seconds before starting polling
  }

  /**
   * Stop polling the session endpoint
   */
  public stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
  }

  /**
   * Check if currently polling
   */
  public getIsPolling(): boolean {
    return this.isPolling;
  }

  /**
   * Send an event to the session endpoint
   * @param sessionId - The session ID
   * @param status - The event type to send
   * @returns Promise<void>
   */
  private async setStatus(sessionId: string, status: string): Promise<void> {
    try {
      const response = await fetch(
        `https://instamandi-kiosk.toolstr.workers.dev/session/${encodeURIComponent(
          sessionId
        )}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status }),
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(5000),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`❌ Error sending status '${status}':`, error);
      throw error;
    }
  }

  /**
   * Check the scan status for a given session
   * @param sessionId - The session ID to check
   * @returns Promise<QRScanStatus>
   */
  private async checkStatus(sessionId: string): Promise<QRScanStatus> {
    const timestamp = Date.now();
    try {
      const response = await fetch(
        `https://instamandi-kiosk.toolstr.workers.dev/session/${encodeURIComponent(
          sessionId
        )}?t=${timestamp}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(5000),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Check if the response indicates the QR code was scanned
      // This depends on the API response format
      const scanned = data.status !== "ready";

      return {
        scanned,
        timestamp: data.timestamp || new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error checking QR scan status:", error);

      // Don't treat network errors as fatal - continue polling
      if (error instanceof Error && error.name === "AbortError") {
        return {
          scanned: false,
          error: "Request timeout",
        };
      }

      return {
        scanned: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Cleanup method to ensure polling is stopped
   */
  public cleanup(): void {
    this.stopPolling();
  }
}

// Export singleton instance
export const qrPollingService = QRPollingService.getInstance();
